import type { ChangeEvent } from "react";

import { SELECT_ALL_VALUE } from "@/core/components/shared/data-table";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import {
  TRANSACTION_STATUSES,
  TRANSACTION_TYPES,
  type TransactionStatus,
  type TransactionType,
} from "@/features/shared/api/enums";
import { useTransactionsTranslation } from "@/features/wallet/i18n/use-transactions-translation";

export interface TransactionsFiltersValue {
  status?: TransactionStatus;
  type?: TransactionType;
  sourceWalletId?: string;
  destinationWalletId?: string;
  productCode?: string;
}

/**
 * Barre de filtres de l'historique des transactions — champs du
 * `TransactionFilter` (Postman `POST /Transactions/all`) : status, type,
 * sourceWalletId, destinationWalletId, productCode.
 * La sentinelle `SELECT_ALL_VALUE` représente « pas de filtre ».
 */
export function TransactionsFilters({
  value,
  onChange,
}: {
  value: TransactionsFiltersValue;
  onChange: (value: TransactionsFiltersValue) => void;
}) {
  const { t } = useTransactionsTranslation();

  const setText =
    (key: "sourceWalletId" | "destinationWalletId" | "productCode") =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;
      onChange({ ...value, [key]: next ? next : undefined });
    };

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="space-y-1.5">
        <Label htmlFor="tx-filter-status">
          {t("wallet.transactions.filters.status")}
        </Label>
        <Select
          value={value.status ?? SELECT_ALL_VALUE}
          onValueChange={(v) =>
            onChange({
              ...value,
              status: v === SELECT_ALL_VALUE ? undefined : (v as TransactionStatus),
            })
          }
        >
          <SelectTrigger id="tx-filter-status" className="w-52">
            <SelectValue placeholder={t("wallet.transactions.filters.all")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SELECT_ALL_VALUE}>
              {t("wallet.transactions.filters.all")}
            </SelectItem>
            {TRANSACTION_STATUSES.map((status) => (
              <SelectItem key={status} value={status} className="notranslate">
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="tx-filter-type">
          {t("wallet.transactions.filters.type")}
        </Label>
        <Select
          value={value.type ?? SELECT_ALL_VALUE}
          onValueChange={(v) =>
            onChange({
              ...value,
              type: v === SELECT_ALL_VALUE ? undefined : (v as TransactionType),
            })
          }
        >
          <SelectTrigger id="tx-filter-type" className="w-64">
            <SelectValue placeholder={t("wallet.transactions.filters.all")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SELECT_ALL_VALUE}>
              {t("wallet.transactions.filters.all")}
            </SelectItem>
            {TRANSACTION_TYPES.map((type) => (
              <SelectItem key={type} value={type} className="notranslate">
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="tx-filter-source">
          {t("wallet.transactions.filters.sourceWalletId")}
        </Label>
        <Input
          id="tx-filter-source"
          className="w-72 font-mono"
          value={value.sourceWalletId ?? ""}
          placeholder={t("wallet.transactions.filters.sourceWalletIdPlaceholder")}
          autoComplete="off"
          onChange={setText("sourceWalletId")}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="tx-filter-destination">
          {t("wallet.transactions.filters.destinationWalletId")}
        </Label>
        <Input
          id="tx-filter-destination"
          className="w-72 font-mono"
          value={value.destinationWalletId ?? ""}
          placeholder={t(
            "wallet.transactions.filters.destinationWalletIdPlaceholder"
          )}
          autoComplete="off"
          onChange={setText("destinationWalletId")}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="tx-filter-product">
          {t("wallet.transactions.filters.productCode")}
        </Label>
        <Input
          id="tx-filter-product"
          className="w-56 font-mono"
          value={value.productCode ?? ""}
          placeholder={t("wallet.transactions.filters.productCodePlaceholder")}
          autoComplete="off"
          onChange={setText("productCode")}
        />
      </div>
    </div>
  );
}
